<template>
    <div v-if="showSection" class="mt-8 p-6 bg-gray-50 rounded-lg border">
        <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold text-gray-800">{{ $t('artistInfo.external.title') }}</h3>
            <div class="size-8 p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full">
                <ArrowPathIcon class="size-6 text-green-500 transition-all hover:text-green-700 cursor-pointer" @click="searchMusicBrainz" :disabled="!artistName"/>
            </div>
        </div>
        
        <div v-if="musicbrainzLoading" class="text-center py-4">
            <div class="text-gray-600">
                <div class="mb-2">{{ $t('artistInfo.external.searching') }}</div>
                <div class="text-sm text-gray-500">{{ $t('artistInfo.external.loading') }}</div>
            </div>
        </div>
        
        <div v-else-if="musicbrainzData" class="space-y-6">
            <!-- Artist Information -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div class="space-y-4">
                    <div v-if="musicbrainzData.name">
                        <Label :label="$t('artistInfo.external.officialName')" />
                        <div class="text-gray-700">{{ musicbrainzData.name }}</div>
                    </div>
                    
                    <div v-if="musicbrainzData.type">
                        <Label :label="$t('artistInfo.external.type')" />
                        <div class="text-gray-700">{{ musicbrainzData.type }}</div>
                    </div>
                    
                    <div v-if="musicbrainzData.country">
                        <Label :label="$t('artistInfo.external.country')" />
                        <div class="text-gray-700">{{ musicbrainzData.country }}</div>
                    </div>
                    
                    <div v-if="musicbrainzData.beginDate || musicbrainzData.endDate">
                        <Label :label="$t('artistInfo.external.activePeriod')" />
                        <div class="text-gray-700">
                            {{ musicbrainzData.beginDate || $t('artistInfo.external.unknown') }} - {{ musicbrainzData.endDate || $t('artistInfo.external.present') }}
                        </div>
                    </div>
                </div>
                
                <div class="space-y-4">
                    <div v-if="musicbrainzData.tags && musicbrainzData.tags.length > 0">
                        <Label :label="$t('artistInfo.tags')" />
                        <div class="flex flex-wrap gap-2">
                            <span v-for="tag in musicbrainzData.tags.slice(0, 5)" :key="tag" 
                                  class="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded">
                                {{ tag }}
                            </span>
                        </div>
                    </div>
                    
                    <div v-if="musicbrainzData.genres && musicbrainzData.genres.length > 0">
                        <Label :label="$t('artistInfo.external.genres')" />
                        <div class="flex flex-wrap gap-2">
                            <span v-for="genre in musicbrainzData.genres.slice(0, 5)" :key="genre" 
                                  class="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                {{ genre }}
                            </span>
                        </div>
                    </div>
                    
                    <div v-if="musicbrainzData.disambiguation">
                        <Label :label="$t('artistInfo.external.disambiguation')" />
                        <div class="text-gray-700 text-sm">{{ musicbrainzData.disambiguation }}</div>
                    </div>
                </div>
            </div>

            <!-- Releases Section -->
            <div v-if="musicbrainzData.releases && musicbrainzData.releases.length > 0" class="mt-6">
                <Label :label="$t('artistInfo.external.recentReleases')" />
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
                    <div v-for="release in musicbrainzData.releases.slice(0, 6)" :key="release.mbid" 
                         class="p-3 bg-white rounded border">
                        <div class="font-medium text-sm">{{ release.title }}</div>
                        <div class="text-xs text-gray-500">{{ release.type }} • {{ release.date || $t('artistInfo.external.unknownDate') }}</div>
                    </div>
                </div>
            </div>

            <!-- Recordings Section -->
            <div v-if="musicbrainzData.recordings && musicbrainzData.recordings.length > 0" class="mt-6">
                <Label :label="$t('artistInfo.external.popularRecordings')" />
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
                    <div v-for="recording in musicbrainzData.recordings.slice(0, 6)" :key="recording.mbid" 
                         class="p-3 bg-white rounded border">
                        <div class="font-medium text-sm">{{ recording.title }}</div>
                        <div class="text-xs text-gray-500">
                            {{ recording.length ? formatDuration(recording.length) : $t('artistInfo.external.unknownDuration') }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- Aliases Section -->
            <div v-if="musicbrainzData.aliases && musicbrainzData.aliases.length > 0" class="mt-6">
                <Label :label="$t('artistInfo.external.alsoKnownAs')" />
                <div class="flex flex-wrap gap-2 mt-2">
                    <span v-for="alias in musicbrainzData.aliases.slice(0, 8)" :key="alias.name" 
                          class="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                        {{ alias.name }}
                    </span>
                </div>
            </div>

            <!-- Members Timeline Section -->
            <div v-if="artistMembers && artistMembers.length > 0" class="mt-6">
                <ArtistMembersTimeline :members="artistMembers" />
            </div>
        </div>
        
        <div v-else class="text-center py-4 text-gray-500">
            <div>{{ $t('artistInfo.external.noData') }}</div>
            <div class="text-sm mt-1">{{ $t('artistInfo.external.searchPrompt') }}</div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits, defineExpose } from 'vue';
import ArtistMembersTimeline from './ArtistMembersTimeline.vue';
import { ArrowPathIcon } from '@heroicons/vue/24/outline'

// Props
const props = defineProps({
    artistName: {
        type: String,
        default: ''
    },
    showSection: {
        type: Boolean,
        default: true
    },
    autoSearch: {
        type: Boolean,
        default: true
    }
});

// Emits
const emit = defineEmits(['data-loaded', 'data-error']);

// Reactive data
const musicbrainzData = ref(null);
const musicbrainzLoading = ref(false);

// Computed
const hasArtistName = computed(() => props.artistName && props.artistName.trim().length > 0);

const artistMembers = computed(() => {
    if (!musicbrainzData.value || !musicbrainzData.value.relations) return [];
    
    // Extract members from relations where type is "member of band"
    return musicbrainzData.value.relations
        .filter(relation => relation.type === 'member of band')
        .map((relation) => {
            const artist = relation.artist;
            const attributes = relation.attributes || [];
            
            // Extract instruments from attributes
            const instruments = attributes
                .filter(attr => attr !== 'additional')
                .join(', ');
            
            return {
                id: artist.id,
                name: artist.name,
                role: 'Member',
                startDate: relation.begin,
                endDate: relation.end,
                ended: relation.ended,
                instruments: instruments || null,
                notes: relation.attribute_values?.additional || null,
                country: artist.country,
                type: artist.type
            };
        });
});

// Methods
const searchMusicBrainz = async () => {
    if (!hasArtistName.value) return;
    
    musicbrainzLoading.value = true;
    musicbrainzData.value = null;
    
    try {
        await fetchMusicBrainzData(props.artistName);
    } catch (error) {
        emit('data-error', error.message);
    } finally {
        musicbrainzLoading.value = false;
    }
};

const fetchMusicBrainzData = async (artistName) => {
    try {
        
        // Search for the artist in MusicBrainz
        const response = await fetch(`/api/artist/musicbrainz/search?q=${encodeURIComponent(artistName)}&limit=1`);
        
        if (response.ok) {
            const data = await response.json();
            
            if (data.data && data.data.length > 0) {
                const artist = data.data[0];
                
                // Get detailed information including releases and recordings
                const detailedResponse = await fetch(`/api/artist/musicbrainz/${artist.mbid}`);
                
                if (detailedResponse.ok) {
                    const detailedData = await detailedResponse.json();
                    musicbrainzData.value = detailedData.data;
                } else {
                    // Fallback to basic artist data
                    musicbrainzData.value = artist;
                }
            } else {
                musicbrainzData.value = null;
            }
        } else {
            console.error('External data search failed:', response.status, response.statusText);
            musicbrainzData.value = null;
        }
    } catch (error) {
        console.error('Error fetching external data:', error);
        musicbrainzData.value = null;
    }
};

const formatDuration = (milliseconds) => {
    if (!milliseconds) return 'Unknown';
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Watch for artist name changes and auto-search if enabled
watch(() => props.artistName, (newName, oldName) => {
    if (props.autoSearch && newName && newName !== oldName && newName.trim().length > 2) {
        searchMusicBrainz();
    }
}, { immediate: false });

// Auto-search on mount if artist name is provided
if (props.autoSearch && hasArtistName.value) {
    searchMusicBrainz();
}

// Expose methods for parent component
defineExpose({
    searchMusicBrainz,
    fetchMusicBrainzData,
    musicbrainzData,
    musicbrainzLoading
});
</script> 